import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { GuestOrder, UserSearchResult } from "../api/models"
import UserFilter from "./widgets/UserFilter"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Support corrections of a PENDING guest order (a store purchase made
// without an account, parked for the address the buyer typed at checkout),
// opened from the row's "…" menu on the sku page's Guest orders tab. Both
// end on a "done" step (the GrantSkuDialog pattern) and hand the updated
// row back through onChanged so the list updates in place. Conditionally
// mounted.

// The buyer mistyped the address: re-key the order to the right one, and
// (by default) email that address the same "sign up or sign in with this
// address" pointer the checkout sent to the wrong one.
export function AmendGuestOrderEmailDialog({ guestOrder, accessToken, onClose, onChanged }: {
  guestOrder: GuestOrder
  accessToken: string
  onClose: () => void
  onChanged: (guestOrder: GuestOrder) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [email, setEmail] = useState("")
  const [resendEmail, setResendEmail] = useState(true)
  const [step, setStep] = useState<"form" | "saving" | "done" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [result, setResult] = useState<{ emailSent: boolean, accountExists: boolean | null } | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"
  const normalized = email.trim().toLowerCase()
  const formValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) && normalized !== guestOrder.email

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      const r = await Api.amendGuestOrderEmail({ skuId: guestOrder.skuId, guestOrderId: guestOrder.id, email: normalized, resendEmail, accessToken })
      setResult({ emailSent: r.emailSent, accountExists: r.accountExists })
      onChanged(r.guestOrder)
      setStep("done")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Amend email</h2>

        {step === "done" && result ? (
          <>
            <p>
              Order #{guestOrder.storeOrderNumber} is now waiting for <strong>{normalized}</strong>.{" "}
              {result.accountExists === true
                ? "An account already uses this address — the purchase lands the next time it loads the app or signs into a plug-in."
                : "It lands the first time an account with this verified address signs in."}
              {resendEmail && (result.emailSent
                ? " The buyer has been emailed what to do."
                : " The email could not be sent — this deployment may not be mailing.")}
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="generate-codes-subtitle">
              Order #{guestOrder.storeOrderNumber} is parked for <strong>{guestOrder.email}</strong>.
              Re-key it to the address the buyer meant — the purchase then lands on the
              account with that verified email the next time it signs in.
            </p>

            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">New email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="generate-codes-input"
                  disabled={busy}
                  autoFocus
                  autoComplete="off"
                  placeholder="buyer@example.com"
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit() }}
                />
              </label>

              <label className="generate-codes-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={resendEmail}
                  onChange={(e) => setResendEmail(e.target.checked)}
                  disabled={busy}
                />
                <span className="generate-codes-label" style={{ margin: 0 }}>Email the new address</span>
                <span className="generate-codes-hint" style={{ margin: 0 }}>
                  the same "sign up or sign in with this address" pointer the checkout sent
                </span>
              </label>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={!formValid || busy} onClick={handleSubmit}>
                {busy ? <><Spinner size={12} /> Saving…</> : "Amend"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// Hand the order to a chosen account outright — what the buyer's own
// sign-in would do (an ordinary order with its entitlement, licenses and
// receipt), for when they cannot or will not sign in with the checkout
// address. Irreversible: from then on it is that account's order.
export function DepositGuestOrderDialog({ guestOrder, accessToken, onClose, onChanged }: {
  guestOrder: GuestOrder
  accessToken: string
  onClose: () => void
  onChanged: (guestOrder: GuestOrder) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [user, setUser] = useState<UserSearchResult | null>(null)
  const [step, setStep] = useState<"form" | "saving" | "done" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"

  const handleSubmit = async () => {
    if (user === null) return
    setStep("saving")
    setErrorMessage("")
    try {
      const r = await Api.depositGuestOrder({ skuId: guestOrder.skuId, guestOrderId: guestOrder.id, userId: user.id, accessToken })
      onChanged(r.guestOrder)
      setStep("done")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog redeem-dialog-overflow"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Deposit to account</h2>

        {step === "done" ? (
          <>
            <p>
              Order #{guestOrder.storeOrderNumber} now belongs to <strong>{user?.email ?? user?.id}</strong>:
              the licenses are in their account and the receipt is on its way.
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="generate-codes-subtitle">
              Order #{guestOrder.storeOrderNumber} is parked for <strong>{guestOrder.email}</strong>.
              Deposit it into an account directly — the account gets the order, its
              licenses and the receipt, exactly as if it had signed in with that address.
              The account's email does not have to match.
            </p>

            <div className="generate-codes-form">
              <div className="generate-codes-field">
                <span className="generate-codes-label">Account</span>
                <UserFilter
                  selected={user}
                  onSelect={setUser}
                  accessToken={accessToken}
                />
              </div>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={user === null || busy} onClick={handleSubmit}>
                {busy ? <><Spinner size={12} /> Depositing…</> : "Deposit"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
